import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, Filter, RotateCcw, Loader2 } from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import LogTimeline from '../pets/components/LogTimeline';
import { usePets } from '../../contexts/PetsContext';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../contexts/HydrationGroupsContext';
import { useFamily } from '../../contexts/FamilyContext';
import { LOG_TYPES } from '../../config/constants';
import { groupLogsByDay } from '../../lib/logs';

/**
 * Bitácora global del hogar (Fase 5).
 *
 * Estructura:
 *  - Filtros arriba: tipo (chips), mascota (dropdown), miembro (dropdown).
 *  - Timeline agrupado por día.
 *  - Botón "Cargar más" al fondo (paginación por cursor).
 *
 * Filtros se aplican client-side. El server solo pagina por timestamp.
 */

const TYPE_GROUPS = {
  all: {
    label: 'Todos',
    types: null,
  },
  feeding: {
    label: 'Alimentación',
    types: [LOG_TYPES.FEEDING, LOG_TYPES.MANUAL_FEEDING],
  },
  inventory: {
    label: 'Inventario',
    types: [
      LOG_TYPES.REFILL,
      LOG_TYPES.MAIN_STOCK_ADD,
      LOG_TYPES.CONFIRM_EMPTY,
      LOG_TYPES.STOCK_ADJUSTMENT,
      LOG_TYPES.BAG_OPENED,
      LOG_TYPES.BAGS_BOUGHT,
    ],
  },
  notes: {
    label: 'Notas y salud',
    types: [LOG_TYPES.NOTE, LOG_TYPES.VET_VISIT, LOG_TYPES.HEALTH],
  },
};

const PAGE_SIZE = 30;

const LogsTimelinePage = () => {
  const { pets, fetchFamilyLogs } = usePets();
  const { groups } = useFeedingGroups();
  const { groups: hydrationGroups } = useHydrationGroups();
  const { members } = useFamily();

  const [allLogs, setAllLogs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [typeFilter, setTypeFilter] = useState('all');
  const [petFilter, setPetFilter] = useState('all');
  const [memberFilter, setMemberFilter] = useState('all');

  const loadFirstPage = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const { logs, nextCursor } = await fetchFamilyLogs({ limit: PAGE_SIZE });
      setAllLogs(logs);
      setCursor(nextCursor);
      setStatus('ready');
    } catch (err) {
      console.error('[LogsTimelinePage] Failed to load logs:', err);
      setError(err.message || 'No se pudo cargar la bitácora.');
      setStatus('error');
    }
  }, [fetchFamilyLogs]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { logs, nextCursor } = await fetchFamilyLogs({
        limit: PAGE_SIZE,
        startAfterDoc: cursor,
      });
      setAllLogs((prev) => [...prev, ...logs]);
      setCursor(nextCursor);
    } catch (err) {
      console.error('[LogsTimelinePage] Failed to load more:', err);
      setError(err.message || 'No se pudieron cargar más eventos.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleResetFilters = () => {
    setTypeFilter('all');
    setPetFilter('all');
    setMemberFilter('all');
  };

  const filteredLogs = useMemo(() => {
    return allLogs.filter((log) => {
      const typeGroup = TYPE_GROUPS[typeFilter];
      if (typeGroup?.types && !typeGroup.types.includes(log.type)) return false;
      if (petFilter !== 'all' && log.petId !== petFilter) return false;
      if (memberFilter !== 'all' && log.userId !== memberFilter) return false;
      return true;
    });
  }, [allLogs, typeFilter, petFilter, memberFilter]);

  const groupedLogs = useMemo(() => groupLogsByDay(filteredLogs), [filteredLogs]);
  const hasFilters = typeFilter !== 'all' || petFilter !== 'all' || memberFilter !== 'all';
  const showLoadMore = cursor != null && status === 'ready';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Bitácora</h1>
        <p className="text-sm text-slate-500">Historial de eventos del hogar</p>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex items-start gap-3 mb-3">
          <div className="rounded-lg bg-slate-100 p-2">
            <Filter className="h-4 w-4 text-slate-600" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-900">Filtros</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Acotá la búsqueda por tipo, mascota o miembro.
            </p>
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              Limpiar
            </button>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">
              Tipo
            </label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(TYPE_GROUPS).map(([key, group]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTypeFilter(key)}
                  className={[
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    typeFilter === key
                      ? 'border-brand-500 bg-brand-50 text-brand-800'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {group.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">
                Mascota
              </label>
              <select
                value={petFilter}
                onChange={(e) => setPetFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="all">Todas</option>
                {pets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">
                Miembro
              </label>
              <select
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="all">Todos</option>
                {members.map((m) => (
                  <option key={m.id ?? m.uid} value={m.id ?? m.uid}>
                    {m.displayName || 'Sin nombre'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Loading inicial */}
      {status === 'loading' && allLogs.length === 0 && (
        <Card className="flex items-center justify-center gap-2 py-8">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" aria-hidden="true" />
          <p className="text-sm text-slate-500">Cargando bitácora...</p>
        </Card>
      )}

      {/* Error */}
      {status === 'error' && (
        <Card>
          <p className="text-sm text-status-red">
            {error || 'No se pudo cargar la bitácora.'}
          </p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={loadFirstPage}>
            Reintentar
          </Button>
        </Card>
      )}

      {/* Empty: hogar nuevo o filtros sin matches */}
      {status === 'ready' && filteredLogs.length === 0 && (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <ClipboardList className="h-10 w-10 text-slate-300" aria-hidden="true" />
          {allLogs.length === 0 ? (
            <p className="text-sm text-slate-500 max-w-sm">
              Todavía no hay eventos. Cuando empieces a alimentar, rellenar o abrir bolsas
              van a aparecer acá.
            </p>
          ) : (
            <div>
              <p className="text-sm text-slate-500 max-w-sm">
                Ningún evento matchea los filtros actuales.
              </p>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={handleResetFilters}
                  leftIcon={RotateCcw}
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Timeline */}
      {status === 'ready' && filteredLogs.length > 0 && (
        <div className="flex flex-col gap-3">
          {groupedLogs.map((day) => (
            <Card key={day.dayStartMs}>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {day.label}
              </h3>
              <LogTimeline
                logs={day.logs}
                members={members}
                pets={pets}
                groups={groups}
                hydrationGroups={hydrationGroups}
              />
            </Card>
          ))}
        </div>
      )}

      {showLoadMore && (
        <Button
          variant="secondary"
          fullWidth
          isLoading={loadingMore}
          onClick={handleLoadMore}
        >
          Cargar más
        </Button>
      )}
    </div>
  );
};

export default LogsTimelinePage;
